import { BoardFilters } from "./filter-chips";
import { ClauseGroup, evaluateJql } from "./jql";
import { displayNameOf } from "./people-directory";
import { IssueCard } from "./types";

// The active view query the local-first board derives columns from. Mirrors the server's
// QueryIssues filter semantics exactly (chips = exact match, q = case-insensitive contains on
// summary/key/assignee, jql via the shared evaluator) so going local changes nothing visible.
export interface ColumnQuery {
  filters: BoardFilters;
  q?: string;
  jql: ClauseGroup | null;
}

// A column groups one or more statuses; cards land in the FIRST column whose status set contains
// their status (overlap is discouraged in the UI but resolved deterministically here).
export interface ColumnSpec {
  id: string;
  statusIds: string[];
}

export interface BoardColumns {
  // columnId → that column's cards, rank-sorted (the same total order the server returns).
  byColumn: Map<string, IssueCard[]>;
  // columnId → card count (the column header badge).
  totalByColumn: Map<string, number>;
}

function matchesChips(card: IssueCard, filters: BoardFilters): boolean {
  if (filters.assignee && card.assigneeUserId !== filters.assignee) return false;
  if (filters.priority && card.priority !== filters.priority) return false;
  if (filters.type && card.type !== filters.type) return false;
  return true;
}

function matchesText(card: IssueCard, q: string | undefined): boolean {
  if (!q) return true;
  const needle: string = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    card.summary.toLowerCase().includes(needle) ||
    card.key.toLowerCase().includes(needle) ||
    displayNameOf(card.assigneeUserId).toLowerCase().includes(needle)
  );
}

function byRank(a: IssueCard, b: IssueCard): number {
  if (a.rank < b.rank) return -1;
  if (a.rank > b.rank) return 1;
  return 0;
}

// Every card passing the active filters (chips + free text + JQL), across all columns.
export function selectFilteredIssues(issues: IssueCard[], query: ColumnQuery): IssueCard[] {
  return issues.filter(
    (card: IssueCard) =>
      matchesChips(card, query.filters) &&
      matchesText(card, query.q) &&
      (query.jql === null || evaluateJql(query.jql, card))
  );
}

// Bucket the filtered set into the board's columns in one pass, each column rank-sorted. A card
// lands in the first column whose status set contains its status; cards whose status maps to no
// column are dropped — matching the server, which only queries the rendered columns' statuses.
export function selectColumns(issues: IssueCard[], columns: ColumnSpec[], query: ColumnQuery): BoardColumns {
  const filtered: IssueCard[] = selectFilteredIssues(issues, query);
  const columnForStatus: Map<string, string> = new Map();
  const byColumn: Map<string, IssueCard[]> = new Map();
  for (const column of columns) {
    byColumn.set(column.id, []);
    for (const statusId of column.statusIds) {
      if (!columnForStatus.has(statusId)) columnForStatus.set(statusId, column.id);
    }
  }
  for (const card of filtered) {
    const columnId: string | undefined = columnForStatus.get(card.statusId);
    if (columnId === undefined) continue;
    byColumn.get(columnId)!.push(card);
  }
  const totalByColumn: Map<string, number> = new Map();
  for (const [id, cards] of byColumn) {
    cards.sort(byRank);
    totalByColumn.set(id, cards.length);
  }
  return { byColumn, totalByColumn };
}

// One column's rank-sorted cards, given the status set it aggregates. Convenience over selectColumns
// for the per-column data source and single-column tests.
export function selectColumnCards(issues: IssueCard[], statusIds: string[], query: ColumnQuery): IssueCard[] {
  const set: Set<string> = new Set<string>(statusIds);
  return selectFilteredIssues(issues, query)
    .filter((card: IssueCard) => set.has(card.statusId))
    .sort(byRank);
}
